import React, { useState, useRef, useEffect, forwardRef } from "react";

/**
 * LazyImage Component
 * Implements Intersection Observer for native lazy loading with fallback to eager loading
 * Includes blur-up effect with low-quality placeholder
 *
 * Props:
 * - src: Main image URL
 * - placeholder: Low-quality/blur placeholder (optional)
 * - alt: Image alt text
 * - className: Tailwind classes to apply to img
 * - containerClassName: Tailwind classes for the wrapper
 * - onLoad: Callback when image loads
 */
const LazyImage = forwardRef(
  (
    {
      src,
      placeholder = null,
      alt = "Image",
      className = "w-full h-full object-cover",
      containerClassName = "relative overflow-hidden h-full w-full",
      onLoad = () => {},
      threshold = 0.1,
    },
    ref,
  ) => {
    const [imageSrc, setImageSrc] = useState(placeholder || src);
    const [isLoaded, setIsLoaded] = useState(!placeholder);
    const [hasError, setHasError] = useState(false);
    const imgRef = useRef(ref);
    const containerRef = useRef(null);

    useEffect(() => {
      // Use IntersectionObserver for lazy loading
      const observerOptions = {
        root: null,
        rootMargin: "50px", // Start loading 50px before element enters viewport
        threshold: threshold,
      };

      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && src) {
            // Element is in viewport, load the image
            if (imgRef.current && imgRef.current.src !== src) {
              imgRef.current.src = src;
            }
            observer.unobserve(entry.target);
          }
        });
      }, observerOptions);

      if (containerRef.current) {
        observer.observe(containerRef.current);
      }

      return () => {
        if (containerRef.current) {
          observer.unobserve(containerRef.current);
        }
      };
    }, [src, threshold]);

    const handleImageLoad = () => {
      setImageSrc(src);
      setIsLoaded(true);
      onLoad();
    };

    const handleImageError = () => {
      setHasError(true);
      onLoad();
    };

    return (
      <div ref={containerRef} className={containerClassName}>
        {/* Placeholder/blur image */}
        {placeholder && !isLoaded && (
          <img
            src={placeholder}
            alt={alt}
            className={`${className} blur-md transition-opacity h-full w-full duration-300 ${!isLoaded ? "opacity-100" : "opacity-0"}`}
            aria-hidden="true"
          />
        )}

        {/* Main image */}
        <img
          ref={imgRef}
          src={placeholder ? undefined : src}
          data-src={src}
          alt={alt}
          className={`${className} transition-opacity h-full w-full duration-500 ${
            isLoaded ? "opacity-100" : "opacity-0"
          }`}
          onLoad={handleImageLoad}
          onError={handleImageError}
          loading="lazy"
          decoding="async"
        />

        {/* Fallback if image fails to load */}
        {hasError && (
          <div
            className={`${className} bg-gray-800 flex items-center justify-center`}
          >
            <span className="text-gray-500 text-xs">Image unavailable</span>
          </div>
        )}

        {/* Loading skeleton */}
        {!isLoaded && !hasError && !placeholder && (
          <div className={`${className} bg-gray-800 animate-pulse`} />
        )}
      </div>
    );
  },
);

LazyImage.displayName = "LazyImage";

export default LazyImage;
